import { Component, computed, effect, inject, input, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, finalize, Subject, debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toast } from 'ngx-sonner';
import { ButtonComponent } from '../../../../../../shared/components/ui/button/button.component';
import { ToggleComponent } from '../../../../../../shared/components/ui/toggle/toggle.component';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { GlobalDateFormatPipe } from '../../../../../../shared/pipes/global-date-format.pipe';
import { ChatbotSecurityService } from '../../../../../../core/services/chatbot-security.service';
import type {
  AiProviderSecurityItem,
  GetAuthTokenStatusResponse,
  ChatbotSecurityUser,
} from '../../../../../../core/models/chatbot-security.model';
import {
  ConfirmationModalComponent,
  type ConfirmationModalData,
} from '../../../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { SelectionComponent, type SelectionOption } from '../../../../../../shared/components/ui/selection/selection.component';
import { DestroyRef } from '@angular/core';

function isValidChatbotId(id: number | null | undefined): id is number {
  return id != null && Number.isFinite(id) && id > 0;
}

@Component({
  selector: 'app-security',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, ToggleComponent, IconComponentV2, GlobalDateFormatPipe, SelectionComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './security.component.html',
})
export class SecurityComponent {
  private readonly securityService = inject(ChatbotSecurityService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly botId = input<number | null>(null);

  readonly pageLoading = signal(true);
  readonly pageError = signal<string | null>(null);
  readonly providers = signal<AiProviderSecurityItem[]>([]);
  readonly authToken = signal<GetAuthTokenStatusResponse | null>(null);

  readonly tokenRevealed = signal(false);
  readonly authActionLoading = signal(false);
  readonly rotateTokenLoading = signal(false);
  /** Remount toggle so it re-syncs with server state after cancel or failed request. */
  readonly authToggleReset = signal(0);
  readonly expandedProviderId = signal<number | null>(null);
  readonly providerKeyInput = signal('');
  readonly providerKeySaving = signal(false);

  private readonly accessUpdate$ = new Subject<number[]>();
  readonly allUsers = signal<ChatbotSecurityUser[]>([]);
  readonly accessUserIds = signal<number[]>([]);
  readonly userSearchQuery = signal('');
  readonly accessManagementLoading = signal(false);
  readonly isUserSelectionExpanded = signal(false);

  readonly userOptions = computed<SelectionOption<number>[]>(() => {
    return this.allUsers().map(u => ({
      value: u.pid,
      label: `${u.firstName} ${u.lastName}`,
    }));
  });

  readonly selectedUsers = computed(() => {
    const ids = this.accessUserIds();
    return this.allUsers().filter(u => ids.includes(u.pid));
  });

  readonly filteredOptions = computed(() => {
    const query = this.userSearchQuery().toLowerCase().trim();
    const options = this.userOptions();
    if (!query) return options;
    return options.filter(o => o.label.toLowerCase().includes(query));
  });

  /** True when at least one listed LLM provider has no API key — chatbot cannot run correctly. */
  readonly hasMissingProviderKeys = computed(() => this.providers().some((p) => !p.keyConfigured));

  private loadSeq = 0;

  constructor() {
    this.accessUpdate$
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((pids) => this.syncAccessWithServer(pids));

    effect((onCleanup) => {
      const id = this.botId();
      if (!isValidChatbotId(id)) {
        untracked(() => {
          this.pageLoading.set(false);
          this.pageError.set(null);
          this.providers.set([]);
          this.authToken.set(null);
          this.tokenRevealed.set(false);
        });
        return;
      }

      const seq = ++this.loadSeq;
      this.pageLoading.set(true);
      this.pageError.set(null);

      const sub = forkJoin({
        providers: this.securityService.getAiProviders(id),
        auth: this.securityService.getAuthTokenStatus(id),
        access: this.securityService.getBotSecurityUserList(id),
        allUsers: this.securityService.getAllUsers(),
      })
        .pipe(
          finalize(() => {
            if (seq === this.loadSeq) {
              this.pageLoading.set(false);
            }
          })
        )
        .subscribe({
          next: ({ providers, auth, access, allUsers }) => {
            if (seq !== this.loadSeq) return;
            this.providers.set(providers.providers ?? []);
            this.authToken.set(auth);
            this.accessUserIds.set(access.userList);
            this.allUsers.set(allUsers.users);
            this.tokenRevealed.set(false);
            this.pageError.set(null);
          },
          error: () => {
            if (seq !== this.loadSeq) return;
            this.pageError.set('Unable to load security settings. Please try again.');
          },
        });

      onCleanup(() => sub.unsubscribe());
    });
  }

  retryLoad(): void {
    const id = this.botId();
    if (!isValidChatbotId(id)) return;
    this.pageLoading.set(true);
    this.pageError.set(null);
    const seq = ++this.loadSeq;
    forkJoin({
      providers: this.securityService.getAiProviders(id),
      auth: this.securityService.getAuthTokenStatus(id),
      access: this.securityService.getBotSecurityUserList(id),
      allUsers: this.securityService.getAllUsers(),
    })
      .pipe(
        finalize(() => {
          if (seq === this.loadSeq) {
            this.pageLoading.set(false);
          }
        })
      )
      .subscribe({
        next: ({ providers, auth, access, allUsers }) => {
          if (seq !== this.loadSeq) return;
          this.providers.set(providers.providers ?? []);
          this.authToken.set(auth);
          this.accessUserIds.set(access.userList);
          this.allUsers.set(allUsers.users);
          this.tokenRevealed.set(false);
          this.pageError.set(null);
        },
        error: () => {
          if (seq !== this.loadSeq) return;
          this.pageError.set('Unable to load security settings. Please try again.');
        },
      });
  }

  invalidBot(): boolean {
    return !isValidChatbotId(this.botId());
  }

  toggleExpandProvider(providerId: number): void {
    if (this.expandedProviderId() === providerId) {
      this.expandedProviderId.set(null);
      this.providerKeyInput.set('');
    } else {
      this.expandedProviderId.set(providerId);
      this.providerKeyInput.set('');
    }
  }

  saveProviderKey(): void {
    const id = this.botId();
    const pid = this.expandedProviderId();
    if (!isValidChatbotId(id) || pid == null) return;
    const apiKey = this.providerKeyInput().trim();
    if (!apiKey) {
      toast.error('Enter an API key');
      return;
    }
    this.providerKeySaving.set(true);
    this.securityService
      .updateAiProviderApiKey(id, { providerId: pid, apiKey })
      .pipe(finalize(() => this.providerKeySaving.set(false)))
      .subscribe({
        next: () => {
          this.expandedProviderId.set(null);
          this.providerKeyInput.set('');
          toast.success('API key saved');
          this.securityService.getAiProviders(id).subscribe({
            next: (r) => this.providers.set(r.providers ?? []),
          });
        },
        error: () => {
          /* micro HTTP already toasts */
        },
      });
  }

  private bumpAuthToggleReset(): void {
    this.authToggleReset.update((n) => n + 1);
  }

  onAuthTokenToggle(wanted: boolean): void {
    if (wanted) {
      this.enableAuthToken();
    } else {
      this.promptDisableAuthToken();
    }
  }

  private enableAuthToken(): void {
    const id = this.botId();
    if (!isValidChatbotId(id)) return;
    this.authActionLoading.set(true);
    this.securityService
      .updateAuthTokenEnabled(id, { enabled: true })
      .pipe(finalize(() => this.authActionLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.success && 'token' in res) {
            this.authToken.set({
              enabled: true,
              token: res.token ?? null,
              expiryDate: res.expiryDate ?? null,
            });
          } else {
            this.refreshAuthOnly(id);
          }
          this.tokenRevealed.set(false);
          toast.success('Auth token enabled. Copy it now — it may not be shown again.');
        },
        error: () => {
          this.bumpAuthToggleReset();
        },
      });
  }

  private promptDisableAuthToken(): void {
    const id = this.botId();
    if (!isValidChatbotId(id)) return;
    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        panelClass: 'confirmation-dialog-panel',
        data: {
          title: 'Disable auth token?',
          description: 'External clients using this token will lose access. You can enable a new token afterward.',
          confirmLabel: 'Disable',
          cancelLabel: 'Cancel',
          variant: 'danger',
        } as ConfirmationModalData,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          this.bumpAuthToggleReset();
          return;
        }
        this.authActionLoading.set(true);
        this.securityService
          .updateAuthTokenEnabled(id, { enabled: false })
          .pipe(finalize(() => this.authActionLoading.set(false)))
          .subscribe({
            next: () => {
              this.tokenRevealed.set(false);
              toast.success('Auth token disabled');
              this.refreshAuthOnly(id);
            },
            error: () => {
              this.bumpAuthToggleReset();
            },
          });
      });
  }

  private refreshAuthOnly(chatbotId: number): void {
    this.securityService.getAuthTokenStatus(chatbotId).subscribe({
      next: (auth) => {
        this.authToken.set(auth);
        this.tokenRevealed.set(false);
      },
    });
  }

  copyToken(value: string): void {
    navigator.clipboard.writeText(value).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Could not copy')
    );
  }

  rotateAuthToken(): void {
    const id = this.botId();
    if (!isValidChatbotId(id)) return;
    this.rotateTokenLoading.set(true);
    this.securityService
      .rotateAuthToken(id)
      .pipe(finalize(() => this.rotateTokenLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.authToken.update((cur) =>
            cur
              ? {
                  ...cur,
                  enabled: true,
                  token: res.token,
                  expiryDate: res.expiryDate,
                }
              : cur
          );
          this.tokenRevealed.set(false);
          toast.success('Token rotated. Copy the new token now.');
        },
        error: () => {
          /* error toast from micro HTTP */
        },
      });
  }

  toggleTokenReveal(): void {
    this.tokenRevealed.update((v) => !v);
  }

  maskedToken(token: string | null): string {
    if (!token) return '••••••••';
    if (token.length <= 8) return '••••••••';
    return `${token.slice(0, 4)}${'•'.repeat(Math.min(24, token.length - 8))}${token.slice(-4)}`;
  }

  isUserInAccessList(pid: number): boolean {
    return this.accessUserIds().includes(pid);
  }

  onSelectionChange(value: number | number[]): void {
    const pids = Array.isArray(value) ? value : [value];
    this.accessUserIds.set(pids);
    this.accessUpdate$.next(pids);
  }

  private syncAccessWithServer(pids: number[]): void {
    const id = this.botId();
    if (!isValidChatbotId(id) || this.pageLoading()) return;

    this.accessManagementLoading.set(true);
    this.securityService
      .updateBotSecurityUserList(id, { userList: pids })
      .pipe(finalize(() => this.accessManagementLoading.set(false)))
      .subscribe({
        next: () => {
          toast.success('Access updated');
        },
        error: () => {
          /* service handled error or already toasted */
        },
      });
  }

  removeUserFromChips(pid: number): void {
    const current = this.accessUserIds().filter((id) => id !== pid);
    this.onSelectionChange(current);
  }
}
